import { domReady } from '../helpers/DOM'
import Lazyable from '../lazyable/Lazyable'
import type WebModel from '../models/WebModel'
import type PrebidFacade from '../prebidFacade/PrebidFacade'
import type SlotFactory from '../slots/SlotFactory'
import SlotRepository from '../slots/SlotRepository'
import { SLOT_RENDER_ATTR } from '../constants'
import { Device } from '../helpers'
export const TRIGGER_SUFFIX = 'waypoint'

/**
 * The Lazy class instantiates both slots and Lazyables
 * for every lazy slotModel received on the WebModel. It
 * is the equivalent to the ATF class for lazy slots.
 * It is NOT responsible for auctioning and displaying the
 * slot. That logic is contained in the Lazyable class.
 */
export default class Lazy {
  lazyables: Array<Lazyable> = []
  constructor(
    readonly model: WebModel,
    readonly slotFactory: SlotFactory,
    readonly prebidFacade: PrebidFacade
  ) {
    // Creates waypoints for lazy slots received from model
    domReady(() => {
      this.render()
    })
  }

  render(): void {
    for (const slotModel of this.model.slots) {
      if (!slotModel.lazy || slotModel.dynamic) {
        continue
      }

      const hintsExist =
        document.querySelectorAll(
          `.mv_slot_target[data-slot=${slotModel.type}]`
        ).length > 0
      if (hintsExist) {
        continue
      }

      // Avoid creating new Slots for adUnits that already exist,
      // except for user-defined Slots which should always be created
      const slotInRepo = SlotRepository.getSlotById(slotModel.adUnitId)
      const slotInRepoIsUserDefined = slotInRepo?.slotModel.userDefined

      if (!slotInRepo || slotInRepoIsUserDefined) {
        // We want to find and insert ads into all the elements that the slotModel's selector finds
        // This could be used to insert recipe ads into two different recipe cards on the same page,
        // for example.
        let selectorElements: Element[]
        const selector =
          Device.isMobileOrTablet && slotModel.mobileSelector
            ? slotModel.mobileSelector
            : slotModel.selector
        if (selector instanceof Element) {
          selectorElements = [selector]
        } else {
          selectorElements = Array.from(document.querySelectorAll(selector))
        }

        selectorElements.forEach((adUnitTarget: HTMLElement) => {
          // Check to see if we've already rendered this slotModel's as into its target.
          const adsAlreadyRendered = adUnitTarget.hasAttribute(
            `${SLOT_RENDER_ATTR}-${slotModel.adUnitId}`
          )
          if (!adsAlreadyRendered) {
            this.slotFactory
              .new({
                ...slotModel,
                selector: adUnitTarget
              })
              .then((slot) => {
                if (!slot || !slot.wrapper) {
                  return
                }

                this.lazyables.push(new Lazyable(slot, this.prebidFacade))
              })
          }
        })
      }
    }
  }
}
